import React, { useState } from 'react';
import { MailWarning } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { sendVerificationEmail } from '../api';

// Tells the signed-in person their email is not verified, with a button to
// send the verification email, and says when the server cannot deliver
// email at all. Hidden while viewing as another user, since the button
// would send to them.
//
// compact: the short form for the user menu; it shows only the unverified
// notice, not the server-wide email note.
export default function EmailVerificationNotice({ compact = false }) {
  const { user, emailVerified, emailEnabled, isImpersonating } = useAuth();
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');

  if (!user || isImpersonating) return null;

  const notDelivered = emailEnabled === false;

  const send = async () => {
    setSending(true);
    setNote('');
    try {
      const res = await sendVerificationEmail();
      if (res.data?.sent) {
        setNote(`Verification email sent to ${user.email}. Open the link in it to finish.`);
      } else {
        setNote(notDelivered
          ? 'Nothing was sent because email is not set up on this server yet.'
          : (res.data?.message || 'Nothing was sent. Try again later.'));
      }
    } catch (err) {
      setNote(err.response?.data?.error || 'Could not send the verification email. Try again.');
    } finally {
      setSending(false);
    }
  };

  if (emailVerified === false) {
    if (compact) {
      return (
        <div style={{ padding: '8px 12px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-warning)', fontWeight: 500 }}>
            <MailWarning size={14} aria-hidden="true" /> Your email isn't verified
          </div>
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: '8px', width: '100%' }} onClick={send} disabled={sending}>
            {sending ? 'Sending...' : 'Send verification email'}
          </button>
          {note && <p role="status" style={{ margin: '6px 0 0', color: 'var(--text-secondary)' }}>{note}</p>}
        </div>
      );
    }
    return (
      <div className="alert alert-warning" role="region" aria-label="Email verification" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <MailWarning size={18} aria-hidden="true" />
          <div style={{ flex: 1, minWidth: '200px' }}>
            <strong>Your email isn't verified.</strong>{' '}
            We need to confirm {user.email} belongs to you.
            {notDelivered && ' Emails are not being delivered yet, so the message will not arrive until an email service is set up.'}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={send} disabled={sending}>
            {sending ? 'Sending...' : 'Send verification email'}
          </button>
        </div>
        {note && <p role="status" style={{ margin: '8px 0 0' }}>{note}</p>}
      </div>
    );
  }

  if (notDelivered && !compact) {
    return (
      <div className="alert alert-warning" role="status" style={{ marginBottom: '16px' }}>
        Emails are not being delivered yet. Invites, password reset links and verification emails are only written to the server log.
        When you invite someone, you get a link to copy and send them yourself.
      </div>
    );
  }

  return null;
}
